/**
 * Menu Hook
 *
 * Custom hook for fetching and managing menu data.
 * Uses TanStack Query for caching and state management.
 * Supports i18n - menus are fetched with current locale for translated labels.
 *
 * @since 2025-12-16
 * @epic INIT-0045, INIT-0046
 */

import { useQuery, useQueryClient } from '@tanstack/react-query';
import { menuApi } from '@/services/menuApi';
import {
  MenuTreeParams,
  toNavMenuItems,
  MenuTypeCode,
} from '@/types/menu';
import { useI18nStore } from '@/store/i18nStore';

/**
 * Query keys for menu data.
 */
export const menuQueryKeys = {
  all: ['menus'] as const,
  types: () => [...menuQueryKeys.all, 'types'] as const,
  tree: (params?: MenuTreeParams) => [...menuQueryKeys.all, 'tree', params] as const,
  treeByCode: (code: string) => [...menuQueryKeys.all, 'tree', 'code', code] as const,
  menu: (id: number) => [...menuQueryKeys.all, 'detail', id] as const,
  children: (parentId: number) => [...menuQueryKeys.all, 'children', parentId] as const,
  ancestors: (id: number) => [...menuQueryKeys.all, 'ancestors', id] as const,
};

/**
 * Hook to fetch menu types.
 *
 * @param status - Optional status filter
 * @returns Query result with menu types
 */
export function useMenuTypes(status?: number) {
  return useQuery({
    queryKey: menuQueryKeys.types(),
    queryFn: () => menuApi.getTypes(status),
    staleTime: 1000 * 60 * 10, // 10 minutes - types rarely change
    gcTime: 1000 * 60 * 60, // 1 hour
  });
}

/**
 * Hook to fetch menu tree.
 * Automatically uses the current locale from i18nStore if not provided.
 *
 * @param params - Tree parameters (including optional locale)
 * @param enabled - Whether to enable the query
 * @returns Query result with menu tree
 */
export function useMenuTree(params?: MenuTreeParams, enabled: boolean = true) {
  const currentLanguage = useI18nStore((state) => state.currentLanguage);
  // Use provided locale or fall back to current language
  const effectiveParams = {
    ...params,
    locale: params?.locale ?? currentLanguage,
  };

  return useQuery({
    // Include locale in query key so it refetches when language changes
    queryKey: [...menuQueryKeys.tree(effectiveParams), effectiveParams.locale],
    queryFn: () => menuApi.getTree(effectiveParams),
    staleTime: 1000 * 60 * 5, // 5 minutes
    gcTime: 1000 * 60 * 30, // 30 minutes
    enabled,
  });
}

/**
 * Chave para cache do menu no localStorage.
 */
const MENU_CACHE_KEY = 'tmf9_menu_cache';

/**
 * Obtém menu do cache do localStorage.
 */
function getMenuFromCache(typeCode: string, locale: string) {
  try {
    const cached = localStorage.getItem(`${MENU_CACHE_KEY}_${typeCode}_${locale}`);
    if (cached) {
      const { data, timestamp } = JSON.parse(cached);
      // Cache válido por 1 hora
      if (Date.now() - timestamp < 1000 * 60 * 60) {
        return data;
      }
    }
  } catch {
    // Ignora erros de parse
  }
  return undefined;
}

/**
 * Salva menu no cache do localStorage.
 */
function saveMenuToCache(typeCode: string, locale: string, data: unknown) {
  try {
    localStorage.setItem(
      `${MENU_CACHE_KEY}_${typeCode}_${locale}`,
      JSON.stringify({ data, timestamp: Date.now() })
    );
  } catch {
    // Ignora erros de storage cheio
  }
}

/**
 * Hook to fetch menu tree by type code.
 * This is the most common use case for sidebar navigation.
 * Automatically uses the current locale from i18nStore.
 * OTIMIZAÇÃO: Usa cache localStorage para carregamento instantâneo.
 *
 * @param typeCode - Menu type code (menu_principal, menu_top, etc)
 * @param enabled - Whether to enable the query
 * @returns Query result with navigation items
 */
export function useMenuTreeByCode(typeCode: MenuTypeCode | string, enabled: boolean = true) {
  const currentLanguage = useI18nStore((state) => state.currentLanguage);

  // Obter dados do cache para carregamento instantâneo
  const cachedData = getMenuFromCache(typeCode, currentLanguage);

  return useQuery({
    // Include locale in query key so it refetches when language changes
    queryKey: [...menuQueryKeys.treeByCode(typeCode), currentLanguage],
    queryFn: async () => {
      const response = await menuApi.getTreeByCode(typeCode, currentLanguage);
      const result = {
        ...response,
        navItems: toNavMenuItems(response),
      };
      // Salvar no cache para próximo carregamento
      saveMenuToCache(typeCode, currentLanguage, result);
      return result;
    },
    staleTime: 1000 * 60 * 5, // 5 minutes
    gcTime: 1000 * 60 * 30, // 30 minutes
    enabled,
    // Usar cache como placeholder para carregamento instantâneo
    placeholderData: cachedData,
  });
}

/**
 * Hook to fetch sidebar menu (menu_principal).
 * Convenience hook for the main sidebar navigation.
 *
 * @returns Query result with navigation items
 */
export function useSidebarMenu() {
  return useMenuTreeByCode('menu_principal');
}

/**
 * Hook to fetch header menu (menu_top).
 * Convenience hook for the top header navigation.
 *
 * @returns Query result with navigation items
 */
export function useHeaderMenu() {
  return useMenuTreeByCode('menu_top');
}

/**
 * Hook to fetch footer menu (menu_bottom).
 * Convenience hook for the footer navigation.
 *
 * @returns Query result with navigation items
 */
export function useFooterMenu() {
  return useMenuTreeByCode('menu_bottom');
}

/**
 * Hook to fetch profile menu (menu_perfil).
 * Convenience hook for the user profile dropdown.
 *
 * @returns Query result with navigation items
 */
export function useProfileMenu() {
  return useMenuTreeByCode('menu_perfil');
}

/**
 * Hook to fetch menu ancestors (breadcrumb).
 *
 * @param menuId - Menu ID
 * @param enabled - Whether to enable the query
 * @returns Query result with ancestor menus
 */
export function useMenuAncestors(menuId: number, enabled: boolean = true) {
  return useQuery({
    queryKey: menuQueryKeys.ancestors(menuId),
    queryFn: () => menuApi.getAncestors(menuId),
    staleTime: 1000 * 60 * 5,
    enabled: enabled && menuId > 0,
  });
}

/**
 * Hook to fetch menu children.
 *
 * @param parentId - Parent menu ID
 * @param enabled - Whether to enable the query
 * @returns Query result with child menus
 */
export function useMenuChildren(parentId: number, enabled: boolean = true) {
  return useQuery({
    queryKey: menuQueryKeys.children(parentId),
    queryFn: () => menuApi.getChildren(parentId),
    staleTime: 1000 * 60 * 5,
    enabled: enabled && parentId > 0,
  });
}

/**
 * Hook to invalidate menu cache.
 * Use this when menus are modified.
 *
 * @returns Function to invalidate menu cache
 */
export function useInvalidateMenuCache() {
  const queryClient = useQueryClient();

  return {
    /**
     * Invalidate all menu queries.
     */
    invalidateAll: () => {
      queryClient.invalidateQueries({ queryKey: menuQueryKeys.all });
    },

    /**
     * Invalidate menu types query.
     */
    invalidateTypes: () => {
      queryClient.invalidateQueries({ queryKey: menuQueryKeys.types() });
    },

    /**
     * Invalidate specific menu tree.
     */
    invalidateTree: (params?: MenuTreeParams) => {
      queryClient.invalidateQueries({ queryKey: menuQueryKeys.tree(params) });
    },

    /**
     * Invalidate menu tree by code.
     */
    invalidateTreeByCode: (code: string) => {
      queryClient.invalidateQueries({ queryKey: menuQueryKeys.treeByCode(code) });
    },
  };
}

/**
 * Combined hook for menu data with common use cases.
 */
export function useMenu() {
  const types = useMenuTypes();
  const sidebarMenu = useSidebarMenu();
  const invalidate = useInvalidateMenuCache();

  return {
    // Menu types
    types: types.data ?? [],
    typesLoading: types.isLoading,
    typesError: types.error,

    // Sidebar menu
    sidebarItems: sidebarMenu.data?.navItems ?? [],
    sidebarLoading: sidebarMenu.isLoading,
    sidebarError: sidebarMenu.error,

    // Invalidation
    invalidateAll: invalidate.invalidateAll,
    invalidateSidebar: () => invalidate.invalidateTreeByCode('menu_principal'),

    // Query hooks for custom use
    useTree: useMenuTree,
    useTreeByCode: useMenuTreeByCode,
    useAncestors: useMenuAncestors,
    useChildren: useMenuChildren,
  };
}

export default useMenu;
